import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Zap, Shield, Server, Globe } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import VisualCanvas from './VisualCanvas';

gsap.registerPlugin(ScrollTrigger);

const StatsSection = ({ statsRef }) => {
    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.from('.stat-number', {
                innerText: 0,
                duration: 2,
                snap: { innerText: 1 },
                stagger: 0.1,
                scrollTrigger: {
                    trigger: statsRef.current,
                    start: 'top 80%',
                }
            });
        }, statsRef);

        return () => ctx.revert();
    }, [statsRef]);

    const stats = [
        { value: '250+', label: 'Projects Completed', icon: Zap },
        { value: '98%', label: 'Happy Clients', icon: Shield },
        { value: '14+', label: 'Years Experience', icon: Server },
        { value: '50k+', label: 'Users Reached', icon: Globe },
    ];

    return (
        <section ref={statsRef} className="py-24 md:py-32 min-h-screen flex items-center bg-slate-50 text-slate-900 relative overflow-hidden">
            {/* 3D Visual Background */}
            <div className="absolute inset-0 z-0 opacity-20 pointer-events-none">
                <VisualCanvas type="sphere" color="#2563eb" height="100%" />
            </div>

            <div className="absolute inset-0 z-0">
                <div className="absolute top-0 right-0 w-1/2 h-full bg-blue-100/20 blur-[100px]"></div>
                <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-purple-100/20 blur-[60px]"></div>
            </div>

            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-10">
                    {stats.map((stat, idx) => {
                        const Icon = stat.icon;
                        return (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.95 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: idx * 0.1 }}
                                className="flex flex-col items-center text-center group bg-white p-5 md:p-12 rounded-2xl md:rounded-[2.5rem] border border-slate-200/60 hover:border-primary/20 transition-all duration-500 shadow-md hover:shadow-xl"
                            >
                                <div className="w-10 h-10 md:w-16 md:h-16 bg-blue-50 rounded-xl flex items-center justify-center mb-4 md:mb-8 border border-blue-100 group-hover:bg-primary group-hover:border-primary transition-all duration-500">
                                    <Icon size={20} className="text-primary group-hover:text-white transition-colors duration-500 md:size-8" />
                                </div>
                                <h3 className="text-2xl md:text-5xl font-black mb-1 md:mb-4 flex items-center tracking-tight">
                                    <span className="stat-number text-slate-900">{parseInt(stat.value)}</span>
                                    <span className="text-primary">{stat.value.replace(/[0-9]/g, '')}</span>
                                </h3>
                                <p className="text-[9px] md:text-xs font-black uppercase tracking-[0.2em] text-slate-500 group-hover:text-slate-900 transition-colors leading-tight">
                                    {stat.label}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default StatsSection;
